// Auth view-model types (post-parser). The seller portal offers two sign-in
// methods on one screen: email + one-time code, and UAE Pass.
//
// The UAE Pass flow has a single entry point: one "Sign in with UAE PASS"
// action resolves — via the callback outcome — into either a login (returning
// user, or an existing email auto-linked server-side) or a sign-up confirmation
// for a brand new seller, which auto-registers after the user confirms their
// mobile. Email sign-in has no sign-up branch: the code is only delivered to an
// address that already has a seller account.

// --- Email one-time-code sign-in ---------------------------------------------

// Handle for an in-flight code. It is the only thing carried between the email
// step and the OTP step — the email itself is display-only from here on, so the
// verified identity stays server-side.
export interface EmailOtpChallenge {
  verificationId: string
  message: string | null
}

// Session issued once the code is verified.
export interface VerifyOtpResult {
  accessToken: string
  refreshToken: string
}

// A resend may rotate the verification id; `verificationId` is null when the
// service kept the existing one.
export interface ResendOtpResult {
  verificationId: string | null
  message: string | null
}

// Mirrors the backend UAE_PASS_CALLBACK_OUTCOME. `email_exists_use_otp` should
// not occur on the seller portal (the backend auto-links matching emails), but
// it is modelled so the callback can fail safe if the backend ever returns it.
export const UAE_PASS_CALLBACK_OUTCOME = {
  LOGIN: 'login',
  EMAIL_EXISTS_USE_OTP: 'email_exists_use_otp',
  SIGNUP_REQUIRED: 'signup_required',
  LINKED: 'linked',
  OWNER_ONBOARDING_VERIFIED: 'owner_onboarding_verified',
} as const

export type UaePassCallbackOutcome =
  (typeof UAE_PASS_CALLBACK_OUTCOME)[keyof typeof UAE_PASS_CALLBACK_OUTCOME]

// Non-sensitive fields to pre-fill the sign-up confirm screen. The verified
// identity behind these (and the UAE Pass `sub`) lives server-side under the
// signup ticket — the client only echoes the ticket + confirmed mobile back.
export interface UaePassSignupPrefill {
  firstName: string
  lastName: string
  email: string
  mobile: string | null
  hasEmiratesId: boolean
}

// Parsed callback result. Only `login` carries tokens; `signup_required`
// carries the ticket + prefill; the other fields are null unless their branch
// is active.
export interface UaePassCallbackResult {
  outcome: UaePassCallbackOutcome
  accessToken: string | null
  refreshToken: string | null
  email: string | null
  message: string | null
  signupTicket: string | null
  verificationTicket: string | null
  prefill: UaePassSignupPrefill | null
}

export interface UaePassAuthorizeUrl {
  authorizeUrl: string
}

// Front-channel UAE Pass single-logout URL fetched when a sign-in dead-ends
// (e.g. the UAE Pass identity is registered on another portal). Redirecting the
// browser here ends the UAE Pass IdP session, so the next attempt presents the
// account chooser instead of silently re-approving the same identity.
export interface UaePassLogoutUrl {
  uaePassLogoutUrl: string
}

export interface UaePassRegisterResult {
  accessToken: string
  refreshToken: string
  hasEmiratesId: boolean
}

// Mobile the user confirms on the sign-up screen, split to match the register
// endpoint contract (E.164 country code + national digits).
export interface UaePassConfirmedMobile {
  mobileCountryCode: string
  mobile: string
}

// Result of a logout. `uaePassLogoutUrl` is set only when the session was a UAE
// Pass login — the app redirects the browser there to end the UAE Pass IdP
// session (front-channel single logout); null for other sessions.
export interface LogoutResult {
  success: boolean
  uaePassLogoutUrl: string | null
}
